<script lang="ts">
	// Svelte
	import { createEventDispatcher, onDestroy, onMount } from 'svelte';
	// Stores
	import { edit } from 'src/stores/ui/edit_lists';
	import { createMemberStatusSnapshot } from 'src/stores/device-runtime/status-selectors';
import { devices } from 'src/stores/lists/devices';
import { rooms } from 'src/stores/lists/rooms';
	import { get } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	// Components
	import {
		Button,
		Container,
		Input,
		Modal,
		Text,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
	} from '@Shelly/shelly-design-system/index';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import ImageList from '../../ImageList.svelte';
	import EmptyRoomList from '../../EmptyRoomList.svelte';
	// Types
	import type { Device } from 'src/types/Device';
	// Helpers
	import { format } from 'svelte-i18n';
	import {
		getImages,
		getImagesByType,
		requestSaveRoom,
		scrollToElement,
		updateWizardStepOnPrevNext,
		updateWizardStepOnWizardItem,
	} from '../../list-helpers';
	import { is_gen_2, isWallDisplay, real_id } from 'src/helpers/interface_helpers';
	import { getAddonStatus } from 'src/helpers/addons/addon_helpers';
	import { getExtSensorsStatus } from 'src/helpers/ext_sensor_helpers';
	import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
	import { isVirtualDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { getVComponentStatus } from 'src/helpers/virtual_helpers/virtual_components_status_helpers';
	import { resetFiltersAllDevicesList } from 'src/stores/ui/filters';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	export let showEditRoomModal = false;
	export let id: string;
	export let type;
	export let list;
	export let currentRoute;

	const dispatch = createEventDispatcher();

	let scrolledToView = false;
	let allCardsRefs;
	let editRoom: any = {
		name: undefined,
		image: '',
	};
	let uploadedImagesDB = [];
	let imagesByRoomType = [];

	// Refs for edit modal for room
	let modalEditRoomRef: Modal = null;
	let wizardItemsContainerRef: HTMLElement = null;
	let wizardContainerRef: HTMLElement = null;
	let selectedSensorRef: HTMLElement = null;

	let wizardItems = [
		{
			active: true,
			activeIndex: 0,
			disabled: false,
			title: $format('wizard_items_details', { default: 'Details' }),
			showMoreDots: false,
			stepNumber: 1,
			tooltipTitle: $format('wizard_items_details', { default: 'Details' }),
		},
		{
			active: false,
			activeIndex: 1,
			disabled: false,
			title: $format('wizard_items_main_sensor', { default: 'Main sensor' }),
			showMoreDots: false,
			stepNumber: 2,
			tooltipTitle: $format('wizard_items_main_sensor', {
				default: 'Main sensor',
			}),
		},
	];

	let activeStep = wizardItems[0];
	$: items = $edit && currentRoute === type ? items : [...list];
	$: isLastStepWizard = activeStep.activeIndex === wizardItems.length - 1;
	$: nextButton = isLastStepWizard ? $format('button_save') : $format('button_next');
	$: selectedSensorRef = allCardsRefs?.[editRoom?.main_sensor];
	$: roomSensorDevices = editRoom?.id ? getRoomSensorDevices(editRoom.id) : [];
	$: sensorDeviceIds = roomSensorDevices.map((device) => device.id).filter(Boolean);
	$: memberStatusStore = createMemberStatusSnapshot(sensorDeviceIds);
	$: memberStatus = $memberStatusStore;
	$: disabled = editRoom?.name?.trim() === '' || typeof editRoom?.name === 'undefined';

	const onClickWizardItem = (index: number) => {
		const result = updateWizardStepOnWizardItem(index, wizardItems, activeStep);
		wizardItems = result.wizardItems;
		activeStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};

	const onInputName = (event: InputEvent) => {
		const target = event.target as HTMLInputElement;
		editRoom = {
			...editRoom,
			name: target.value,
		};
	};

	let removeContainerEventListener = () => {};

	const scrollSelectedCardToView = () => {
		removeContainerEventListener();

		let elementRef = null;
		let wizardStepIndex = 0;

		if (activeStep.activeIndex === 1 && selectedSensorRef && !scrolledToView) {
			elementRef = selectedSensorRef;
			wizardStepIndex = 1;
		}

		if (!elementRef) {
			modalEditRoomRef.scrollToTop();
		} else {
			removeContainerEventListener = scrollToElement(
				elementRef,
				wizardItemsContainerRef,
				activeStep.activeIndex,
				wizardStepIndex,
			);
		}

		scrolledToView = true;
	};

	const getRoomSensorDevices = (roomID: number) => {
		const roomSensorDevices: Device[] = Object.values($devices)
			.filter(
				(device: Device) =>
					(roomID === -1 || device.room_id === roomID) && hasRoomSensorBehavior(device),
			)
			.map((device) => {
				return {
					...device,
					name: getSelectCardDeviceName(device),
				};
			});

		if (roomSensorDevices.length > 0) {
			roomSensorDevices.unshift({
				id: false,
				name: 'None',
				icon: 'fal fa-ban',
			} as any);
		}

		return roomSensorDevices;
	};

	const hasRoomSensorBehavior = (device: Device): boolean => {
		const isSensorCategory = device.category === 'sensor' || device.category === 'thermostat';
		const isVirtualSensorLike =
			(isVirtualDevice(device.type) || isXT1Module(device.type)) &&
			getVComponentStatus(device).some((status) => !status?.hasButton);
		const isAddonSensorLike = is_gen_2(device)
			? getAddonStatus(device).some((addon) => addon?.sensor?.type !== 'switch')
			: getExtSensorsStatus(device).length > 0;
		return (
			isSensorCategory || isVirtualSensorLike || isWallDisplay(device.type) || isAddonSensorLike
		);
	};

	const getSelectCardDeviceName = (device) => {
		return device.name + (!memberStatus[real_id(device.id)]?.online ? ' - Offline' : '');
	};

	const onSelectImage = (e: CustomEvent) => {
		editRoom = {
			...editRoom,
			image: e.detail.image,
			backgroundColor: e.detail.backgroundColor,
		};
	};

	const onUploadImage = (e: CustomEvent) => {
		onSelectImage(e);
		uploadedImagesDB = [...e.detail.uploadedImagesDB];
	};

	const onSelectMainSensor = (deviceID) => {
		editRoom = {
			...editRoom,
			main_sensor: deviceID,
		};
	};

	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		const result = updateWizardStepOnPrevNext(type, wizardItems, activeStep);
		wizardItems = result.wizardItems;
		activeStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};

	const validateRoomName = (room) => {
		const isEmpty = room.name === '';
		// roomNameValidationMsg = isEmpty
		// 	? $format('input_error_empty_name', { default: 'Name cannot be empty' })
		// 	: '';

		return {
			isValid: !isEmpty,
		};
	};

	const setRoomsEditChanges = (currentEditRoom) => {
		const currentRooms = get(rooms);

		rooms.set({
			...currentRooms,
			[currentEditRoom.id]: currentEditRoom,
		});

		dispatch('edited-room', currentEditRoom);
	};

	const handleAddEditRoomFinish = async () => {
		editRoom.name = editRoom.name.trim();
		let currentEditRoom = {
			...editRoom,
		};

		if (!validateRoomName(currentEditRoom).isValid) {
			return;
		}

		await requestSaveRoom(currentEditRoom, showEditRoomModal);

		setRoomsEditChanges(currentEditRoom);

		onCloseEditRoom();
	};

	const onCloseEditRoom = () => {
		showEditRoomModal = false;

		// Clean wizard steps states
		const result = updateWizardStepOnWizardItem(0, wizardItems, activeStep);
		wizardItems = result.wizardItems;
		activeStep = { ...result.activeWizardStep };
	};

	const fetchUploadedImadesDB = async () => {
		uploadedImagesDB = await getImages('room');
	};

	$: if (showEditRoomModal) {
		fetchUploadedImadesDB();

		if (id !== undefined && id !== '-1' && $rooms[id]) {
			editRoom = $rooms[id];
		}
	}

	onMount(() => {
		imagesByRoomType = getImagesByType('room', null);
	});

	onDestroy(() => {
		resetFiltersAllDevicesList();
	});
</script>

<Modal
	on:close={onCloseEditRoom}
	bind:showModal={showEditRoomModal}
	title={$format('prompt_title_edit_room', { default: 'Edit room' })}
	bind:this={modalEditRoomRef}
	removeScrollPadding
>
	<Wizard slot="toolbar">
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={() => onClickWizardItem(i)}
				active={item.active}
				title={item.title}
				disabled={item.disabled}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<div slot="content">
		<WizardContainer
			activeStepIndex={activeStep.activeIndex}
			adaptiveHeight
			bind:wizardItemsContainerRef
			bind:wizardContainerRef
		>
			{#each wizardItems as item, i (i)}
				<!-- DO NOT REMOVE COMMENT: Padding references -->
				<WizardContent active={activeStep.activeIndex === i} class="pt-s pb-s">
					{#if i === 0}
						<Container noScroll padding="0">
							<Text
								element="h2"
								class={classMap({
									'mb-s': true,
									'text-500': $showNewLayout,
								})}
							>
								{$format('room_name', { default: 'Room Name' })}
							</Text>
							<Input
								aria-labelledby="input-15x1sdc"
								icon="fas fa-door-open"
								label={$format('label_name', { default: 'name' })}
								value={editRoom.name}
								error={editRoom.name === ''
									? $format('input_error_empty_name', {
											default: 'Name cannot be empty',
										})
									: ''}
								on:input={onInputName}
								id="input-15x1sdc"
							/>
							<hr class="mb-l mt-l" />
						</Container>

						<!-- DO NOT REMOVE COMMENT: Padding references -->
						<Container noScroll padding="0">
							<Text
								element="h2"
								class={classMap({
									'mb-m': true,
									'text-500': $showNewLayout,
								})}
							>
								{$format('select_image', { default: 'Select image' })}
							</Text>

							<ImageList
								{uploadedImagesDB}
								imagesByTypeDB={imagesByRoomType}
								selectedImage={editRoom.image}
								selectedBackgroundColor={editRoom.backgroundColor}
								requestType="room"
								on:select-image={onSelectImage}
								on:upload-image={onUploadImage}
							/>
						</Container>
					{:else if i === 1}
						<Container noScroll padding="0">
							{#if roomSensorDevices.length > 0}
								{@const selectedId = roomSensorDevices.find(
									(d) => d.id === editRoom.main_sensor,
								)
									? editRoom.main_sensor
									: false}
								<SelectList
									title={$format('select_main_sensor', {
										default: 'Select main sensor',
									})}
									list={roomSensorDevices}
									selectedCardsId={[{ id: selectedId }]}
									variant="radio"
									bind:allCardsRefs
									on:click={(e) => onSelectMainSensor(e.detail.id)}
								/>
							{:else}
								<EmptyRoomList />
							{/if}
						</Container>
					{/if}
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			disabled={activeStep.activeIndex === 0}
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			icon="fas fa-chevron-left"
			variant="secondary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => onClickPrevNextButton('prev')}
		>
			{$format('button_back')}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon={isLastStepWizard ? 'fas fa-save' : ''}
			rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => {
				if (isLastStepWizard) {
					handleAddEditRoomFinish();
				} else {
					onClickPrevNextButton('next');
				}
			}}
			disabled={isLastStepWizard && disabled}
		>
			{nextButton}
		</Button>
	</div>
</Modal>
